import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Chip, Dialog, Slide, ToggleButtonGroup, ToggleButton } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { fetchData, postData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import AttendanceCalendar from "./AttendanceCalendar";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const fallbackRows = [
  { id: 1, studentName: "Aarav Kumar",  regNo: "REG001", grade: "Grade 3", attendance: "Present", time: "08:30 AM", applicationId: "APP001", requestDate: "2025-05-01", consecutiveAbsent: 0 },
  { id: 2, studentName: "Priya Sharma", regNo: "REG002", grade: "Grade 3", attendance: "Absent",  time: "—",        applicationId: "APP002", requestDate: "2025-05-01", consecutiveAbsent: 3 },
  { id: 3, studentName: "Karthik Raj",  regNo: "REG003", grade: "Grade 4", attendance: "Present", time: "08:45 AM", applicationId: "APP003", requestDate: "2025-05-02", consecutiveAbsent: 0 },
  { id: 4, studentName: "Divya Nair",   regNo: "REG004", grade: "Grade 4", attendance: "Present", time: "08:55 AM", applicationId: "APP004", requestDate: "2025-05-02", consecutiveAbsent: 0 },
  { id: 5, studentName: "Sanjay Patel", regNo: "REG005", grade: "Grade 5", attendance: "Absent",  time: "—",        applicationId: "APP005", requestDate: "2025-05-03", consecutiveAbsent: 2 },
];

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

export default function Attendance() {
  const [rows, setRows] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [viewMode, setViewMode] = useState("day");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [mandateSending, setMandateSending] = useState(null); // studentId currently sending

  const loadAttendance = useCallback(async (date) => {
    try {
      const response = await fetchData(`teacher/attendance/list?date=${date}`);
      if (response?.data && Array.isArray(response.data)) {
        setRows(response.data);
      } else {
        setRows(fallbackRows);
      }
    } catch (_) {
      setRows(fallbackRows);
    }
  }, []);

  useEffect(() => {
    loadAttendance(selectedDate);
  }, [loadAttendance, selectedDate]);

  const toggleAttendance = useCallback(async (student) => {
    const newStatus = student.attendance === "Present" ? "Absent" : "Present";
    try {
      await postData("teacher/attendance/update", {
        applicationId: student.applicationId,
        date: selectedDate,
        attendance: newStatus,
      });
    } catch (_) {}
    setRows((prev) =>
      prev.map((r) => (r.id === student.id ? { ...r, attendance: newStatus } : r))
    );
    setToast({ open: true, severity: "success", message: `Attendance updated to ${newStatus}` });
  }, [selectedDate]);

  const handleApprove = useCallback(async (row) => {
    try {
      await putData(`teacher/attendance/approve/${row.id}`, {});
      setToast({ open: true, severity: "success", message: `Attendance approved for ${row.studentName}` });
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Approval failed" });
    }
  }, []);

  /* ── Gap #16: Send Mandate Form for 2+ consecutive absent students ── */
  const handleSendMandateForm = useCallback(async (row) => {
    setMandateSending(row.id);
    try {
      await postData("teacher/attendance/send-mandate", {
        studentId: row.id,
        applicationId: row.applicationId,
        parentEmail: row.parentEmail || "",
        studentName: row.studentName,
        consecutiveAbsent: row.consecutiveAbsent,
      });
      setToast({ open: true, severity: "success", message: `Mandate form notification sent for ${row.studentName}` });
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Failed to send mandate form notification" });
    } finally {
      setMandateSending(null);
    }
  }, []);

  const openHistory = (student) => {
    setSelectedStudent(student);
    setCalendarOpen(true);
  };

  const presentCount = rows.filter((r) => r.attendance === "Present").length;
  const absentCount = rows.filter((r) => r.attendance === "Absent").length;
  const consecutiveAlertCount = rows.filter((r) => (r.consecutiveAbsent || 0) >= 2).length;

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Attendance
      </p>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0 }}>Attendance</h2>
          <Chip label={`${presentCount} Present`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
          <Chip label={`${absentCount} Absent`} size="small" sx={{ backgroundColor: "#ffebee", color: "#f44336", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
          {consecutiveAlertCount > 0 && (
            <Chip label={`${consecutiveAlertCount} Consecutive Alert`} size="small" sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Button startIcon={<FileDownloadIcon />} sx={actionBtnStyle}>Export</Button>
        </Box>
      </Box>

      {/* ─── View Mode Toggle ─── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v) setViewMode(v); }}
          size="small"
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "13px", px: 2, border: "1px solid #D5D7DA", color: "#8F95B2" },
            "& .Mui-selected": { backgroundColor: "#244C77 !important", color: "#fff !important", borderColor: "#244C77 !important" } }}
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="year">Year</ToggleButton>
        </ToggleButtonGroup>

        {viewMode === "day" && (
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", fontSize: "13px", padding: "6px 10px", cursor: "pointer", outline: "none" }}
          />
        )}

        {viewMode === "week" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "#8F95B2" }}>From:</Typography>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", fontSize: "13px", padding: "6px 10px", cursor: "pointer", outline: "none" }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "#8F95B2" }}>To:</Typography>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", fontSize: "13px", padding: "6px 10px", cursor: "pointer", outline: "none" }}
              />
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 8px" }}>
          <thead>
            <tr style={{ backgroundColor: "#8F95B2" }}>
              {["Student Name", "Stu ID", "Grade", "Request Date", "Toggle", "Attendance", "Time", "Action"].map((h, i) => (
                <th key={i} style={{ color: "#fff", padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "13px",
                  borderRadius: i === 0 ? "8px 0 0 0" : i === 7 ? "0 8px 0 0" : "0" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const hasConsecutiveAlert = (row.consecutiveAbsent || 0) >= 2;
              return (
                <tr key={idx}
                  style={{ backgroundColor: hasConsecutiveAlert ? "#fff8f0" : "#fff", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hasConsecutiveAlert ? "#fff0d9" : "#DAD8FC"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = hasConsecutiveAlert ? "#fff8f0" : "#fff"; }}>
                  <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <span>{row.studentName}</span>
                      {hasConsecutiveAlert && (
                        <span style={{ fontSize: "11px", color: "#e65100", fontWeight: "bold" }}>
                          ⚠ {row.consecutiveAbsent} consecutive absences
                        </span>
                      )}
                    </Box>
                  </td>
                  <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.regNo}</td>
                  <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.grade}</td>
                  <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.requestDate || row.date || "—"}</td>
                  <td style={{ padding: "12px" }}>
                    <Box sx={{ display: "flex", gap: "4px" }}>
                      <Button
                        size="small"
                        onClick={() => toggleAttendance({ ...row, attendance: "Absent" })}
                        sx={{
                          minWidth: "32px", height: "32px", borderRadius: "6px", fontWeight: "bold", fontSize: "12px", textTransform: "none",
                          backgroundColor: row.attendance === "Present" ? "#4caf50" : "#e8f5e9",
                          color: row.attendance === "Present" ? "#fff" : "#4caf50",
                          "&:hover": { backgroundColor: "#4caf50", color: "#fff" },
                        }}
                      >P</Button>
                      <Button
                        size="small"
                        onClick={() => toggleAttendance({ ...row, attendance: "Present" })}
                        sx={{
                          minWidth: "32px", height: "32px", borderRadius: "6px", fontWeight: "bold", fontSize: "12px", textTransform: "none",
                          backgroundColor: row.attendance === "Absent" ? "#f44336" : "#ffebee",
                          color: row.attendance === "Absent" ? "#fff" : "#f44336",
                          "&:hover": { backgroundColor: "#f44336", color: "#fff" },
                        }}
                      >A</Button>
                    </Box>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <Chip
                      label={row.attendance}
                      size="small"
                      sx={{
                        backgroundColor: row.attendance === "Present" ? "#e8f5e9" : "#ffebee",
                        color: row.attendance === "Present" ? "#4caf50" : "#f44336",
                        fontWeight: "bold", fontSize: "12px", borderRadius: "20px",
                      }}
                    />
                  </td>
                  <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.time}</td>
                  <td style={{ padding: "12px" }}>
                    <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        onClick={() => openHistory(row)}
                        sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", borderRadius: "8px", textTransform: "none", fontSize: "12px", fontWeight: "bold", padding: "4px 12px" }}
                      >History</Button>
                      <Button
                        size="small"
                        startIcon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => handleApprove(row)}
                        sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", borderRadius: "8px", textTransform: "none", fontSize: "12px", fontWeight: "bold", padding: "4px 10px" }}
                      >Approve</Button>
                      {/* ── Gap #16: Send Mandate Form button ── */}
                      {hasConsecutiveAlert && (
                        <Button
                          size="small"
                          startIcon={<SendIcon sx={{ fontSize: "13px !important" }} />}
                          onClick={() => handleSendMandateForm(row)}
                          disabled={mandateSending === row.id}
                          sx={{
                            backgroundColor: "#fff3e0", color: "#e65100",
                            borderRadius: "8px", textTransform: "none",
                            fontSize: "12px", fontWeight: "bold",
                            padding: "4px 10px",
                            border: "1px solid #ffb74d",
                            "&:hover": { backgroundColor: "#ffe0b2" },
                          }}
                        >
                          {mandateSending === row.id ? "Sending…" : "Mandate"}
                        </Button>
                      )}
                    </Box>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} TransitionComponent={Transition} fullWidth maxWidth="md">
        <AttendanceCalendar
          open={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          student={selectedStudent}
        />
      </Dialog>
    </Box>
  );
}
